import { useLocation } from "wouter";
import { useEffect, useState } from "react";
import { useFeature } from "@/lib/useFeature";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useListCoaches, useCreateContract, useGetContract, useListContractTemplates, useAddContractClause, getGetContractQueryKey, getListContractsQueryKey, getListCoachesQueryKey, getListContractTemplatesQueryKey } from "@workspace/api-client-react";
import type { ContractTemplate } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { DEFAULT_TERMINATION_RULES } from "@workspace/buyout";
import { useOrg } from "@/lib/orgContext";
import { handleApiError } from "@/lib/handleApiError";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, DollarSign, FileText, Shield, Calendar, RefreshCw, Settings, Gift, Car, Loader2, AlertCircle, Sparkles, Layers, Search, Building2, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "wouter";
import { useAuth } from "@clerk/react";
import {
  BuyoutTermsFields,
  cleanBuyoutSchedule,
  type BuyoutTermsValue,
} from "@/components/buyout-terms-editor";
import { BonusRepeater, AllowanceRepeater, type ContractBonus, type ContractAllowance } from "@/components/bonus-repeater";
import { CustomFieldsSection, saveCustomFieldValues } from "@/components/CustomFieldsSection";
import { getApplicableTemplates, type ContractTypeKey } from "@/lib/bonus-templates";
import { randomId } from "@/lib/utils";
import { COACH_POSITIONS, COACH_POSITION_VALUES, toSelectablePosition } from "@/lib/coachPositions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";


const DRAFT_STORAGE_KEY = "coach-contracts:contract-new-draft:v1";

const formSchema = z.object({
  coachId: z.coerce.number().min(1, "Select a coach"),
  contractType: z.enum(COACH_POSITION_VALUES),
  status: z.enum(["pending", "active"]),
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required"),
  annualSalary: z.coerce.number().min(0, "Salary must be positive"),
  buyoutClause: z.coerce.number().min(0).optional().default(0),
  terminationConditions: z.string().optional(),
  notes: z.string().optional(),
});

export default function NewContract() {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { org, loading: orgLoading, error: orgError, refetch: refetchOrg } = useOrg();
  const { getToken } = useAuth();
  const draftFeature = useFeature("contracts.draft");
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});
  const {
    data: coaches,
    isLoading: coachesLoading,
    isError: coachesIsError,
    refetch: refetchCoaches,
  } = useListCoaches(undefined, {
    query: { enabled: !!org, queryKey: getListCoachesQueryKey() },
  });
  const createContract = useCreateContract();

  const [buyoutTerms, setBuyoutTerms] = useState<BuyoutTermsValue>({
    schedule: [],
    rules: DEFAULT_TERMINATION_RULES,
    mitigationRequired: false,
  });

  const [bonuses, setBonuses] = useState<ContractBonus[]>([]);
  const [allowances, setAllowances] = useState<ContractAllowance[]>([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [templateMode, setTemplateMode] = useState<"replace" | "append">("append");

  // 4-step wizard with localStorage draft persistence (see effects below).
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const WIZARD_STEPS: { id: 1 | 2 | 3 | 4; label: string; fields: Array<keyof z.infer<typeof formSchema>> }[] = [
    { id: 1, label: "Basic Info",     fields: ["coachId", "contractType", "status", "startDate", "endDate"] },
    { id: 2, label: "Compensation",   fields: ["annualSalary", "buyoutClause"] },
    { id: 3, label: "Clauses",        fields: ["terminationConditions"] },
    { id: 4, label: "Review & Submit", fields: [] },
  ];
  const goNextStep = async () => {
    const fields = WIZARD_STEPS[step - 1].fields;
    const ok = fields.length === 0 ? true : await form.trigger(fields as Array<keyof z.infer<typeof formSchema>>);
    if (ok) {
      setStep((s) => (Math.min(4, s + 1) as 1 | 2 | 3 | 4));
      // Scroll next step into view.
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    } else {
      toast({
        title: "Please fix the highlighted fields",
        description: "Some required fields on this step still need your attention.",
        variant: "destructive",
      });
    }
  };
  const goBackStep = () => {
    setStep((s) => (Math.max(1, s - 1) as 1 | 2 | 3 | 4));
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerRoleFilter, setPickerRoleFilter] = useState("all");
  const [pickerSportFilter, setPickerSportFilter] = useState("all");
  const [pickerDivisionFilter, setPickerDivisionFilter] = useState("all");
  const [appliedContractTemplate, setAppliedContractTemplate] = useState<ContractTemplate | null>(null);
  const [templateClauseRefs, setTemplateClauseRefs] = useState<Array<{ clauseId: number; clauseVersionId: number; position: number }>>([]);

  const { data: contractTemplates = [] } = useListContractTemplates(undefined, {
    query: { enabled: !!org, queryKey: getListContractTemplatesQueryKey() },
  });

  const addContractClause = useAddContractClause();

  const pickerSports = Array.from(new Set(contractTemplates.map((t) => t.sport).filter(Boolean)));

  const filteredPickerTemplates = contractTemplates.filter((t) => {
    // Wizard creates contracts; term-sheet templates belong to /term-sheets/new.
    const matchesDocKind = (t.documentKind ?? "contract") === "contract";
    if (!matchesDocKind) return false;
    const matchesSearch = !pickerSearch || t.name.toLowerCase().includes(pickerSearch.toLowerCase()) || (t.sport ?? "").toLowerCase().includes(pickerSearch.toLowerCase());
    const matchesRole = pickerRoleFilter === "all" || t.role === pickerRoleFilter;
    const matchesSport = pickerSportFilter === "all" || (pickerSportFilter === "any" ? !t.sport : t.sport === pickerSportFilter);
    const matchesDivision = pickerDivisionFilter === "all" || (pickerDivisionFilter === "any" ? !t.institutionType : t.institutionType === pickerDivisionFilter);
    return matchesSearch && matchesRole && matchesSport && matchesDivision;
  });

  useEffect(() => {
    if (org && org.role !== "admin") {
      toast({ title: "Access denied", description: "Viewers cannot create contracts.", variant: "destructive" });
      setLocation("/contracts");
    }
  }, [org, setLocation, toast]);

  // Demo orgs showcase the full product (drafting included) and must never
  // see the pricing/upgrade gate during a live sales demo.
  if (org && !org.isDemo && !draftFeature.enabled) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link href="/contracts"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Draft New Contract</h1>
            <p className="text-muted-foreground mt-1">Create a new coaching contract from scratch.</p>
          </div>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-8 text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-[hsl(220,65%,12%)] text-white flex items-center justify-center">
            <Sparkles className="h-7 w-7" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-1">Draft plan feature</div>
            <h2 className="text-xl font-bold text-gray-900">Contract Drafting</h2>
            <p className="mt-2 text-gray-500 max-w-md mx-auto text-sm leading-relaxed">
              Creating new contracts from scratch requires the <strong>Draft</strong> plan. Your current <strong className="capitalize">{draftFeature.currentPlan}</strong> plan includes contract tracking and the renewals pipeline, but not authoring.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button asChild className="bg-[hsl(220,65%,12%)] hover:bg-[hsl(220,65%,7%)] text-white">
              <Link href="/settings#plans">Upgrade to Draft — from $1,799/mo</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link href="/contracts">Back to Contracts</Link>
            </Button>
          </div>
          <p className="text-xs text-gray-400">14-day free trial · Cancel anytime</p>
        </div>
      </div>
    );
  }

  const prefill = (() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    const p = new URLSearchParams(search);
    return {
      coachId: p.get("coachId") ? Number(p.get("coachId")) : 0,
      annualSalary: p.get("annualSalary") ? Number(p.get("annualSalary")) : 0,
      renewalId: p.get("renewalId") ?? "",
      fromContractId: p.get("fromContractId") ? Number(p.get("fromContractId")) : 0,
      // Promote-from-term-sheet: prefill key terms and link via termSheetId.
      fromTermSheet: p.get("fromTermSheet") ? Number(p.get("fromTermSheet")) : 0,
    };
  })();

  const { data: priorContract } = useGetContract(prefill.fromContractId, {
    query: {
      enabled: prefill.fromContractId > 0,
      queryKey: getGetContractQueryKey(prefill.fromContractId),
    },
  });

  const { data: sourceTermSheet } = useGetContract(prefill.fromTermSheet, {
    query: {
      enabled: prefill.fromTermSheet > 0,
      queryKey: getGetContractQueryKey(prefill.fromTermSheet),
    },
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      coachId: prefill.coachId,
      contractType: "head_coach",
      status: "pending",
      startDate: "",
      endDate: "",
      annualSalary: prefill.annualSalary,
      buyoutClause: 0,
      terminationConditions: prefill.renewalId ? "Renewal of prior contract." : "",
      notes: prefill.renewalId ? `Promoted from renewal #${prefill.renewalId}.` : "",
    },
  });

  useEffect(() => {
    if (prefill.coachId || prefill.annualSalary || prefill.renewalId) {
      toast({ title: "Prefilled from renewal", description: "Review and adjust terms before saving." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  void location;

  // Promote-from-term-sheet: copy headline terms (salary midpoint, signing bonus, notes).
  const [termSheetHydrated, setTermSheetHydrated] = useState(false);
  useEffect(() => {
    if (termSheetHydrated || !sourceTermSheet || prefill.fromTermSheet <= 0) return;
    const ts = sourceTermSheet as unknown as {
      coachId?: number;
      coachName?: string | null;
      contractType?: string;
      salaryRangeMin?: number | null;
      salaryRangeMax?: number | null;
      annualSalary?: number | null;
      keyTerms?: string | null;
      bonuses?: Array<{ amount?: number | null; label?: string | null; customLabel?: string | null }>;
    };
    const salaryGuess =
      ts.salaryRangeMin && ts.salaryRangeMax
        ? Math.round((ts.salaryRangeMin + ts.salaryRangeMax) / 2)
        : ts.salaryRangeMax ?? ts.salaryRangeMin ?? ts.annualSalary ?? 0;
    const coachLabel = ts.coachName?.trim() || "the coach";
    form.reset({
      coachId: ts.coachId ?? 0,
      contractType: toSelectablePosition(ts.contractType),
      status: "pending",
      startDate: "",
      endDate: "",
      annualSalary: salaryGuess || 0,
      buyoutClause: 0,
      terminationConditions: "",
      notes: `${coachLabel} — promoted from term sheet (#${prefill.fromTermSheet}).${ts.keyTerms ? `\n\nKey terms from sheet:\n${ts.keyTerms}` : ""}`,
    });
    if (ts.bonuses && ts.bonuses.length > 0) {
      // Map term-sheet bonuses to ContractBonus (defaults: type=signing, timing=one_time_signing).
      setBonuses(
        ts.bonuses.map((b): ContractBonus => ({
          id: randomId(),
          type: "signing",
          label: b.label ?? "Signing Bonus",
          customLabel: b.customLabel ?? undefined,
          amount: b.amount ?? 0,
          condition: "",
          paymentTiming: "one_time_signing",
          isRecurring: false,
        })),
      );
    }
    setTermSheetHydrated(true);
    toast({ title: "Prefilled from term sheet", description: "Review headline terms and complete the remaining fields." });
  }, [sourceTermSheet, prefill.fromTermSheet, termSheetHydrated, form, toast]);

  // Renewal flow handles its own prefill — skip draft restore/save.
  const isPrefilledFromRenewal =
    !!prefill.coachId || !!prefill.annualSalary || !!prefill.renewalId || prefill.fromContractId > 0;

  // Hydrate saved draft on mount (skipped during renewal prefill).
  const [draftRestored, setDraftRestored] = useState(false);
  useEffect(() => {
    if (draftRestored) return;
    if (isPrefilledFromRenewal) {
      setDraftRestored(true);
      return;
    }
    try {
      const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) {
        setDraftRestored(true);
        return;
      }
      const draft = JSON.parse(raw) as {
        values?: Partial<z.infer<typeof formSchema>>;
        step?: 1 | 2 | 3 | 4;
        bonuses?: ContractBonus[];
        allowances?: ContractAllowance[];
        buyoutTerms?: BuyoutTermsValue;
        templateClauseRefs?: Array<{ clauseId: number; clauseVersionId: number; position: number }>;
        savedAt?: number;
      };
      if (draft.values) {
        form.reset({ ...form.getValues(), ...draft.values });
      }
      if (draft.bonuses?.length)        setBonuses(draft.bonuses);
      if (draft.allowances?.length)     setAllowances(draft.allowances);
      if (draft.buyoutTerms)            setBuyoutTerms(draft.buyoutTerms);
      if (draft.templateClauseRefs?.length) setTemplateClauseRefs(draft.templateClauseRefs);
      if (draft.step)                   setStep(draft.step);
      const savedDate = draft.savedAt ? new Date(draft.savedAt).toLocaleString() : null;
      toast({
        title: "Draft restored",
        description: savedDate
          ? `Picked up where you left off (saved ${savedDate}).`
          : "Picked up where you left off.",
      });
    } catch {
      // Corrupt draft — clear and continue with a fresh form.
      try { window.localStorage.removeItem(DRAFT_STORAGE_KEY); } catch { /* ignore */ }
    } finally {
      setDraftRestored(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Wizard draft autosave ───────────────────────────────────────────
  // Once the initial restore has run, mirror every meaningful change
  // back into localStorage. Using form.watch() as a dependency proxy
  // means we re-run when any field changes — a debounce isn't strictly
  // necessary because localStorage writes are cheap and the payload is
  // small.
  const watchedAll = form.watch();
  useEffect(() => {
    if (!draftRestored) return;
    if (isPrefilledFromRenewal) return;
    try {
      window.localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify({
          values: watchedAll,
          step,
          bonuses,
          allowances,
          buyoutTerms,
          templateClauseRefs,
          savedAt: Date.now(),
        }),
      );
    } catch {
      // Quota exceeded or storage disabled — silently degrade; the
      // wizard still works in-memory.
    }
  }, [draftRestored, isPrefilledFromRenewal, watchedAll, step, bonuses, allowances, buyoutTerms, templateClauseRefs]);

  const clearWizardDraft = () => {
    try { window.localStorage.removeItem(DRAFT_STORAGE_KEY); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!priorContract) return;
    if (!priorContract.startDate || !priorContract.endDate) return;
    const priorStart = new Date(priorContract.startDate);
    const priorEnd = new Date(priorContract.endDate);
    const termMs = priorEnd.getTime() - priorStart.getTime();
    const newStart = new Date(priorEnd);
    newStart.setUTCDate(newStart.getUTCDate() + 1);
    const newEnd = new Date(newStart.getTime() + termMs);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);

    form.reset({
      coachId: priorContract.coachId,
      contractType: toSelectablePosition(priorContract.contractType),
      status: "pending",
      startDate: fmt(newStart),
      endDate: fmt(newEnd),
      annualSalary: priorContract.annualSalary ?? 0,
      buyoutClause: priorContract.buyoutClause ?? 0,
      terminationConditions: priorContract.terminationConditions ?? "",
      notes: (() => {
        const subject = priorContract.coachName
          ? `${priorContract.coachName}'s prior contract (#${priorContract.id})`
          : `prior contract #${priorContract.id}`;
        return prefill.renewalId
          ? `Renewed from ${subject}${prefill.renewalId ? ` (renewal #${prefill.renewalId})` : ""}.`
          : `Renewed from ${subject}.`;
      })(),
    });

    if (priorContract.bonuses && priorContract.bonuses.length > 0) {
      setBonuses(priorContract.bonuses as ContractBonus[]);
    }
    if (priorContract.allowances && priorContract.allowances.length > 0) {
      setAllowances(priorContract.allowances as ContractAllowance[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorContract]);

  const watchedCoachId = form.watch("coachId");
  const watchedContractType = form.watch("contractType");
  const selectedCoach = coaches?.find((c) => c.id === Number(watchedCoachId));
  const applicableTemplates = getApplicableTemplates(
    selectedCoach?.sport,
    watchedContractType as ContractTypeKey | undefined,
  );

  const handleLoadTemplate = () => {
    const tpl = applicableTemplates.find((t) => t.id === selectedTemplateId);
    if (!tpl) return;
    const newRows: ContractBonus[] = tpl.bonuses.map((b) => ({ ...b, id: randomId() }));
    if (templateMode === "replace") {
      setBonuses(newRows);
    } else {
      setBonuses([...bonuses, ...newRows]);
    }
    setTemplateDialogOpen(false);
    setSelectedTemplateId("");
    toast({
      title: "Template loaded",
      description: `Added ${newRows.length} bonus row${newRows.length === 1 ? "" : "s"} from "${tpl.name}".`,
    });
  };

  const watchedSalary = form.watch("annualSalary") || 0;
  const watchedBuyout = form.watch("buyoutClause") || 0;
  const totalBonuses = bonuses.reduce((s, b) => s + (b.amount || 0), 0);
  const totalAllowances = allowances.reduce((s, a) => s + (a.amount || 0), 0);

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    // Wizard guard — only the final step is allowed to submit. Pressing
    // Enter on an intermediate step (or any other implicit submit) just
    // advances rather than creating the contract.
    if (step !== 4) {
      void goNextStep();
      return;
    }
    const payload = {
      ...values,
      bonuses,
      allowances,
      buyoutSchedule: cleanBuyoutSchedule(buyoutTerms.schedule),
      terminationRules: buyoutTerms.rules,
      mitigationRequired: buyoutTerms.mitigationRequired,
      ...(prefill.fromTermSheet > 0 ? { termSheetId: prefill.fromTermSheet } : {}),
    };
    createContract.mutate({ data: payload }, {
      onSuccess: async (contract) => {
        // Contract was created — discard the working wizard draft so a
        // fresh "/contracts/new" load starts blank.
        clearWizardDraft();

        const hasCustomFields = Object.keys(customFieldValues).length > 0;
        if (hasCustomFields) {
          const token = await getToken();
          if (token) {
            await saveCustomFieldValues("contract", contract.id, customFieldValues, token);
          }
        }

        const clauseRefsToAttach = templateClauseRefs;
        if (clauseRefsToAttach.length > 0) {
          const attachPromises = clauseRefsToAttach.map((ref) =>
            addContractClause.mutateAsync({
              contractId: contract.id,
              data: { clauseId: ref.clauseId, clauseVersionId: ref.clauseVersionId, position: ref.position },
            })
          );
          Promise.allSettled(attachPromises).then((results) => {
            const failed = results.filter((r) => r.status === "rejected").length;
            if (failed > 0) {
              toast({
                title: "Contract created — some clauses not attached",
                description: `${failed} of ${clauseRefsToAttach.length} template clause${clauseRefsToAttach.length === 1 ? "" : "s"} could not be attached. You can add them manually from the contract detail page.`,
                variant: "destructive",
              });
            } else {
              toast({
                title: "Contract Drafted",
                description: `Created with ${clauseRefsToAttach.length} clause${clauseRefsToAttach.length === 1 ? "" : "s"} from template.`,
              });
            }
            queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
            setLocation(`/contracts/${contract.id}`);
          });
        } else {
          toast({ title: "Contract Drafted", description: "Contract has been successfully created." });
          queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
          setLocation(`/contracts/${contract.id}`);
        }
      },
      onError: (err: unknown) => {
        handleApiError(toast, err, { fallbackTitle: "Could not create contract" });
      },
    });
  };

  const handleApplyTemplate = (template: ContractTemplate) => {
    const df = template.defaultFields ?? {};
    const d = df as Record<string, unknown>;
    setAppliedContractTemplate(template);
    setTemplateClauseRefs((template.clauseRefs ?? []) as Array<{ clauseId: number; clauseVersionId: number; position: number }>);
    if (d.annualSalary != null) form.setValue("annualSalary", Number(d.annualSalary));
    if (d.buyoutClause != null) form.setValue("buyoutClause", Number(d.buyoutClause));
    if (d.terminationConditions) form.setValue("terminationConditions", String(d.terminationConditions));
    if (d.notes) form.setValue("notes", String(d.notes));
    if (template.role && (COACH_POSITION_VALUES as readonly string[]).includes(template.role)) {
      form.setValue("contractType", template.role as (typeof COACH_POSITION_VALUES)[number]);
    }
    toast({ title: "Template applied", description: `Pre-filled from "${template.name}". Review and adjust terms before saving.` });
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/contracts"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Draft New Contract</h1>
          <p className="text-muted-foreground mt-1">Complete all sections to create a new coaching contract.</p>
        </div>
      </div>

      {!priorContract && contractTemplates.length > 0 && (
        <Card className={appliedContractTemplate ? "border-primary/30 bg-primary/5" : ""}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-base">
                  {appliedContractTemplate ? (
                    <>Template applied: <span className="text-primary">{appliedContractTemplate.name}</span></>
                  ) : "Start from a template"}
                </CardTitle>
              </div>
              {appliedContractTemplate ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground h-7"
                  onClick={() => {
                    setAppliedContractTemplate(null);
                    setTemplateClauseRefs([]);
                  }}
                >
                  Clear template
                </Button>
              ) : null}
            </div>
            {!appliedContractTemplate && (
              <CardDescription>Pick a role-based template to pre-fill defaults. You can still edit everything.</CardDescription>
            )}
          </CardHeader>
          {!appliedContractTemplate && (
            <CardContent className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[160px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search templates…" className="pl-9 h-8 text-sm" value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} />
                </div>
                <Select value={pickerRoleFilter} onValueChange={setPickerRoleFilter}>
                  <SelectTrigger className="h-8 text-sm w-full sm:w-[160px]">
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All roles</SelectItem>
                    <SelectItem value="head_coach">Head Coach</SelectItem>
                    <SelectItem value="assistant_coach">Assistant Coach</SelectItem>
                    <SelectItem value="coordinator">Coordinator</SelectItem>
                    <SelectItem value="director_of_ops">Dir. of Operations</SelectItem>
                    <SelectItem value="olympic_sport_head_coach">Olympic Sport HC</SelectItem>
                  </SelectContent>
                </Select>
                {pickerSports.length > 0 && (
                  <Select value={pickerSportFilter} onValueChange={setPickerSportFilter}>
                    <SelectTrigger className="h-8 text-sm w-full sm:w-[140px]">
                      <SelectValue placeholder="All sports" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sports</SelectItem>
                      <SelectItem value="any">No sport (any)</SelectItem>
                      {pickerSports.map((s) => (
                        <SelectItem key={s!} value={s!}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={pickerDivisionFilter} onValueChange={setPickerDivisionFilter}>
                  <SelectTrigger className="h-8 text-sm w-full sm:w-[140px]">
                    <SelectValue placeholder="All divisions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All divisions</SelectItem>
                    <SelectItem value="any">Any (no division)</SelectItem>
                    <SelectItem value="public_d1">Public D1</SelectItem>
                    <SelectItem value="public_d2">Public D2</SelectItem>
                    <SelectItem value="public_d3">Public D3</SelectItem>
                    <SelectItem value="private_d1">Private D1</SelectItem>
                    <SelectItem value="private_d2">Private D2</SelectItem>
                    <SelectItem value="private_d3">Private D3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {filteredPickerTemplates.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2 text-center">No templates match — try adjusting filters.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {filteredPickerTemplates.map((t) => {
                    const df = t.defaultFields ?? {} as Record<string, unknown>;
                    const d = df as Record<string, unknown>;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleApplyTemplate(t)}
                        className="flex items-start gap-3 rounded-lg border bg-card hover:bg-muted/30 hover:border-primary/40 transition-colors p-3 text-left group"
                      >
                        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                          <Layers className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{t.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 truncate">
                            {t.sport ? `${t.sport} · ` : ""}{d.annualSalary != null ? `$${Number(d.annualSalary).toLocaleString()} base · ` : ""}{t.clauseRefs?.length ?? 0} clauses
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0 mt-1" />
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {priorContract && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3">
          <RefreshCw className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-indigo-900 flex items-center gap-1.5 flex-wrap">
              <span>
                {priorContract.coachName
                  ? `Renewed from ${priorContract.coachName}'s contract`
                  : "Renewed from prior contract"}
              </span>
              <span
                className="font-mono text-[10px] text-indigo-700 bg-white/70 border border-indigo-200 rounded px-1 py-px"
                title={`Contract ID #${priorContract.id}`}
              >
                #{priorContract.id}
              </span>
            </div>
            <div className="text-sm text-indigo-700 mt-0.5">
              Prior term {priorContract.startDate} → {priorContract.endDate}. Salary, bonuses, allowances, buyout, and termination conditions have been carried over. Review and adjust before saving.
            </div>
            <div className="mt-2">
              <Link
                href={`/contracts/${priorContract.id}`}
                className="text-sm text-indigo-700 underline hover:text-indigo-900"
              >
                View prior contract
              </Link>
            </div>
          </div>
        </div>
      )}

      {watchedSalary > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-xs text-blue-600 font-medium mb-1">Annual Value</div>
            <div className="text-xl font-bold text-blue-800">${Number(watchedSalary).toLocaleString()}</div>
          </div>
          {totalBonuses > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-xs text-green-600 font-medium mb-1">Total Bonuses</div>
              <div className="text-xl font-bold text-green-800">${totalBonuses.toLocaleString()}</div>
            </div>
          )}
          {Number(watchedBuyout) > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="text-xs text-orange-600 font-medium mb-1">Buyout Clause</div>
              <div className="text-xl font-bold text-orange-800">${Number(watchedBuyout).toLocaleString()}</div>
            </div>
          )}
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

          {/* ── Wizard stepper ───────────────────────────────────────── */}
          <ol
            className="flex items-stretch gap-1 sm:gap-2 rounded-xl border bg-card px-3 py-3 sm:px-4"
            data-testid="contract-wizard-stepper"
            aria-label="Contract creation steps"
          >
            {WIZARD_STEPS.map((s) => {
              const isCompleted = s.id < step;
              const isCurrent = s.id === step;
              return (
                <li key={s.id} className="flex-1 min-w-[110px]">
                  <button
                    type="button"
                    onClick={async () => {
                      // Allow jumping back freely; jumping forward requires
                      // upstream steps to be valid.
                      if (s.id < step) {
                        setStep(s.id);
                        return;
                      }
                      if (s.id === step) return;
                      // Validate all intermediate steps' fields before
                      // jumping forward, so the user can't skip required
                      // input by clicking ahead in the stepper.
                      const fieldsToValidate = WIZARD_STEPS
                        .filter((x) => x.id >= step && x.id < s.id)
                        .flatMap((x) => x.fields) as Array<keyof z.infer<typeof formSchema>>;
                      const ok = fieldsToValidate.length === 0
                        ? true
                        : await form.trigger(fieldsToValidate);
                      if (ok) setStep(s.id);
                      else toast({ title: "Finish the current step first", description: "Some required fields still need attention.", variant: "destructive" });
                    }}
                    className={cn(
                      "group w-full text-left rounded-lg px-2.5 py-2 transition-colors",
                      "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    )}
                    data-testid={`wizard-step-${s.id}`}
                    aria-current={isCurrent ? "step" : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold border",
                          isCompleted && "bg-green-600 text-white border-green-600",
                          isCurrent && "bg-primary text-primary-foreground border-primary",
                          !isCompleted && !isCurrent && "bg-muted text-muted-foreground border-border",
                        )}
                      >
                        {isCompleted ? <Check className="h-3.5 w-3.5" /> : s.id}
                      </span>
                      <span className={cn("text-xs sm:text-sm font-medium truncate", !isCurrent && !isCompleted && "text-muted-foreground")}>
                        {s.label}
                      </span>
                    </div>
                    <div
                      className={cn(
                        "mt-2 h-1 rounded-full",
                        isCompleted && "bg-green-600",
                        isCurrent && "bg-primary",
                        !isCompleted && !isCurrent && "bg-muted",
                      )}
                    />
                  </button>
                </li>
              );
            })}
          </ol>

          {step === 1 && (<>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-muted-foreground" /> Contract Parties & Type</CardTitle>
              <CardDescription>Identify the coach and define the scope of this contract.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="coachId"
                  render={({ field }) => {
                    const orgBlocked = orgLoading || !!orgError;
                    const isLoadingCoaches = orgLoading || (!!org && coachesLoading);
                    const hasError = !!orgError || coachesIsError;
                    const placeholder = orgLoading
                      ? "Loading workspace…"
                      : orgError
                        ? "Workspace unavailable"
                        : coachesLoading
                          ? "Loading coaches…"
                          : "Select a coach";
                    return (
                      <FormItem>
                        <FormLabel>Coach *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ? String(field.value) : undefined}
                          disabled={orgBlocked || coachesLoading || coachesIsError}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {isLoadingCoaches && (
                                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                                )}
                                <SelectValue placeholder={placeholder} />
                              </div>
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {coaches?.map((coach) => (
                              <SelectItem key={coach.id} value={String(coach.id)}>
                                {coach.firstName} {coach.lastName} — {coach.sport}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {hasError && (
                          <div className="mt-2 flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                            <AlertCircle className="h-4 w-4 shrink-0" />
                            <span className="flex-1">
                              {orgError
                                ? "Could not load your workspace — retry"
                                : "Could not load coaches — retry"}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (orgError) {
                                  refetchOrg();
                                } else {
                                  refetchCoaches();
                                }
                              }}
                            >
                              Retry
                            </Button>
                          </div>
                        )}
                        {!hasError && !isLoadingCoaches && coaches && coaches.length === 0 && (
                          <FormDescription>
                            No coaches yet. Add one from the Coaches page first.
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="contractType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contract Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COACH_POSITIONS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Initial Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pending">Pending — Draft / Under Review</SelectItem>
                          <SelectItem value="active">Active — Executed & In Effect</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>Set to Pending if contract is still being reviewed.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date *</FormLabel>
                      <FormControl><Input type="date" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date *</FormLabel>
                      <FormControl><Input type="date" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>
          </>)}

          {step === 2 && (<>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-muted-foreground" /> Base Compensation</CardTitle>
              <CardDescription>Define the base salary and buyout clause.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="annualSalary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base Annual Salary ($) *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-2.5 text-muted-foreground text-sm">$</span>
                          <Input type="number" className="pl-7" placeholder="0" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="buyoutClause"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Buyout Clause ($)</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-2.5 text-muted-foreground text-sm">$</span>
                          <Input type="number" className="pl-7" placeholder="0" {...field} />
                        </div>
                      </FormControl>
                      <FormDescription>Amount payable if university terminates without cause.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-muted-foreground" /> Bonuses</CardTitle>
                  <CardDescription>Add structured bonus line items — signing, performance, academic, or any other bonus type. Each row captures the trigger condition, amount, and payment timing.</CardDescription>
                </div>
                <Dialog
                  open={templateDialogOpen}
                  onOpenChange={(open) => {
                    setTemplateDialogOpen(open);
                    if (open) {
                      setSelectedTemplateId("");
                      setTemplateMode(bonuses.length > 0 ? "append" : "replace");
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="gap-2 shrink-0" data-testid="button-load-template">
                      <Sparkles className="h-4 w-4" />
                      Load template
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Load bonus template</DialogTitle>
                      <DialogDescription>
                        {selectedCoach?.sport
                          ? <>Showing templates for <strong>{selectedCoach.sport}</strong> + this contract type, plus generic options.</>
                          : <>Select a coach above to see sport-specific templates. Generic templates are shown below.</>}
                      </DialogDescription>
                    </DialogHeader>

                    {applicableTemplates.length === 0 ? (
                      <div className="text-sm text-muted-foreground py-4">
                        No templates match the current sport and contract type.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <RadioGroup value={selectedTemplateId} onValueChange={setSelectedTemplateId} className="space-y-2 max-h-72 overflow-y-auto pr-1">
                          {applicableTemplates.map((tpl) => (
                            <label
                              key={tpl.id}
                              htmlFor={`tpl-${tpl.id}`}
                              className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                                selectedTemplateId === tpl.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                              }`}
                              data-testid={`template-option-${tpl.id}`}
                            >
                              <RadioGroupItem value={tpl.id} id={`tpl-${tpl.id}`} className="mt-1" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm">{tpl.name}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">{tpl.description}</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {tpl.bonuses.length} bonus row{tpl.bonuses.length === 1 ? "" : "s"}
                                </div>
                              </div>
                            </label>
                          ))}
                        </RadioGroup>

                        {bonuses.length > 0 && (
                          <div className="space-y-2 border-t pt-3">
                            <Label className="text-xs">When loading:</Label>
                            <RadioGroup
                              value={templateMode}
                              onValueChange={(v) => setTemplateMode(v as "replace" | "append")}
                              className="flex flex-col gap-2"
                            >
                              <label htmlFor="mode-append" className="flex items-center gap-2 text-sm cursor-pointer">
                                <RadioGroupItem value="append" id="mode-append" />
                                Append to existing {bonuses.length} row{bonuses.length === 1 ? "" : "s"}
                              </label>
                              <label htmlFor="mode-replace" className="flex items-center gap-2 text-sm cursor-pointer">
                                <RadioGroupItem value="replace" id="mode-replace" />
                                Replace existing rows
                              </label>
                            </RadioGroup>
                          </div>
                        )}
                      </div>
                    )}

                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={handleLoadTemplate}
                        disabled={!selectedTemplateId || applicableTemplates.length === 0}
                        data-testid="button-confirm-load-template"
                      >
                        Load template
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <BonusRepeater bonuses={bonuses} onChange={setBonuses} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-muted-foreground" /> Allowances & Add-ons</CardTitle>
              <CardDescription>Add fixed perks and allowances — vehicle, housing, country club, courtesy car, etc. These are separate from performance-based bonuses.</CardDescription>
            </CardHeader>
            <CardContent>
              <AllowanceRepeater allowances={allowances} onChange={setAllowances} />
            </CardContent>
          </Card>
          </>)}

          {step === 3 && (<>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-muted-foreground" /> Termination & Legal Provisions</CardTitle>
              <CardDescription>Define conditions under which this contract may be ended.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="terminationConditions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Termination Conditions</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="e.g. 90 days written notice required. Termination for cause includes: NCAA violations, moral turpitude, willful misconduct, breach of contract. Termination without cause triggers buyout clause. Coach may terminate with 60 days notice with forfeit of signing bonus."
                        className="min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-muted-foreground" /> Buyout Terms (calculator inputs)
              </CardTitle>
              <CardDescription>
                Define the per-year buyout schedule, which formula applies to each termination type,
                and whether the contract requires the coach to mitigate damages. These values feed
                the buyout calculator on the contract detail page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BuyoutTermsFields value={buyoutTerms} onChange={setBuyoutTerms} />
            </CardContent>
          </Card>
          </>)}

          {step === 4 && (<>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-muted-foreground" /> Additional Provisions & Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Special Provisions / Internal Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any additional contract provisions, side agreements, or internal notes for the compliance team..."
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* ── Review summary ──────────────────────────────────────── */}
          <Card data-testid="wizard-review-summary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Check className="h-5 w-5 text-muted-foreground" /> Review</CardTitle>
              <CardDescription>Double-check the key terms below, then create the contract.</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">Coach</dt>
                  <dd className="font-medium">
                    {(() => {
                      const id = form.watch("coachId");
                      const coach = coaches?.find((c) => c.id === Number(id));
                      return coach ? `${coach.firstName} ${coach.lastName} — ${coach.sport}` : "—";
                    })()}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">Contract type</dt>
                  <dd className="font-medium capitalize">{(form.watch("contractType") ?? "—").replace("_", " ")}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">Term</dt>
                  <dd className="font-medium">{form.watch("startDate") || "—"} → {form.watch("endDate") || "—"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">Initial status</dt>
                  <dd className="font-medium capitalize">{form.watch("status") ?? "—"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">Annual salary</dt>
                  <dd className="font-medium">${(Number(form.watch("annualSalary")) || 0).toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">Buyout clause</dt>
                  <dd className="font-medium">${(Number(watchedBuyout) || 0).toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">Bonuses</dt>
                  <dd className="font-medium">{bonuses.length} row{bonuses.length === 1 ? "" : "s"} · ${totalBonuses.toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">Allowances</dt>
                  <dd className="font-medium">{allowances.length} row{allowances.length === 1 ? "" : "s"} · ${totalAllowances.toLocaleString()}</dd>
                </div>
                {templateClauseRefs.length > 0 && (
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground text-xs uppercase tracking-wide">Template clauses</dt>
                    <dd className="font-medium">{templateClauseRefs.length} clause{templateClauseRefs.length === 1 ? "" : "s"} will be attached after creation</dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          <CustomFieldsSection
            entityType="contract"
            values={customFieldValues}
            onChange={(key, value) => setCustomFieldValues((prev) => ({ ...prev, [key]: value }))}
          />
          </>)}

          {/* ── Wizard navigation ───────────────────────────────────── */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pb-8" data-testid="wizard-nav">
            <Button variant="ghost" type="button" onClick={() => setLocation("/contracts")}>Cancel</Button>
            <div className="flex gap-2 sm:ml-auto">
              {step > 1 && (
                <Button variant="outline" type="button" onClick={goBackStep} data-testid="wizard-back">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              )}
              {step < 4 ? (
                <Button type="button" onClick={() => void goNextStep()} data-testid="wizard-next">
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button type="submit" disabled={createContract.isPending} size="lg" data-testid="wizard-submit">
                  {createContract.isPending ? "Creating..." : "Create Contract"}
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
